import React, { Fragment, useState, useEffect } from 'react';
import get from 'lodash/get';
import head from 'lodash/head';
import Input from '@nintex-ux/react-core/dist/Input';
import Select from '@nintex-ux/react-core/dist/Select';
import Button from '@nintex-ux/react-core/dist/Button';
import Tooltip from '@nintex-ux/react-core/dist/Tooltip';
import useFetch from '../../utils/useFetch';
import Registry from '../../components/Registry/Registry';
import Jobs from '../../components/Jobs/Jobs';

let page = 0;
const MERGE_AMOUNT = 5;
const UP = 'UP';
const DOWN = 'DOWN';

const getOccurenceCount = job => get(job, 'metadata.OccurrenceCount');

const mergeHeadAndTailOfPages = (jobsForNewPage, jobsOfPreviousPage) => {
  if (!jobsForNewPage.length || !jobsOfPreviousPage.length) return jobsForNewPage;

  const numberOfNewJobGroupsToCheck =
    jobsForNewPage.length >= MERGE_AMOUNT ? MERGE_AMOUNT : jobsForNewPage.length;
  const lastFewjobGroupsOfPreviousPage = jobsOfPreviousPage.slice(-MERGE_AMOUNT);

  const firstFewJobGroupsForNewPage = jobsForNewPage
    .slice(0, numberOfNewJobGroupsToCheck)
    .map((group, i) => {
      const occurenceCount = getOccurenceCount(group[0]);
      const mergedGroup = lastFewjobGroupsOfPreviousPage
        .map(j => (getOccurenceCount(j[0]) === occurenceCount ? [...group, ...j] : null))
        .filter(j => j);

      return head(mergedGroup) || group;
    });

  return [...firstFewJobGroupsForNewPage, ...jobsForNewPage.slice(numberOfNewJobGroupsToCheck)];
};

const scroll = direction => () => {
  if (direction === UP) {
    window.scrollTo(0, 0);
  } else {
    window.scrollTo(0, document.body.scrollHeight);
  }
};

function Home() {
  const [platform, setPlatform] = useState('');
  const [scheduleCollection, setScheduleCollection] = useState('');
  const [scheduleName, setScheduleName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [errors, setErrors] = useState({});
  const [jobsData, setJobsData] = useState([]);
  const registry = useFetch();
  const jobs = useFetch();

  const continuationToken = get(jobs.response, 'data.continuationToken');

  useEffect(() => {
    const responseJobs = get(jobs.response, 'data.jobs', []);
    if (jobs.isLoading || !responseJobs.length) return;

    const jobsOfPreviousPage = jobsData.length ? jobsData[jobsData.length - 1].jobs : [];
    const jobsForNewPage = mergeHeadAndTailOfPages(responseJobs, jobsOfPreviousPage);

    const newJobs = {
      page: ++page,
      jobs: jobsForNewPage,
    };
    setJobsData(currentState => [...currentState, newJobs]);
  }, [jobs.isLoading]);

  const getUrl = (type, options = {}) => {
    const base = `/api/Schedule`;
    const common = `platform=${platform.value}&scheduleCollection=${scheduleCollection}&scheduleName=${scheduleName}`;
    const jobsContinuationToken = options.continuationToken
      ? `&continuationToken=${options.continuationToken}`
      : '';

    switch (type) {
      case 'jobs':
        return `${base}/Jobs?${common + jobsContinuationToken}`;
      case 'registry':
        return `${base}/Registry?${common}`;
      case 'history':
        return `${base}/history?${common}&jobId=${options.jobId}`;
      case 'definition':
        return `${base}/definition?name=${options.definitionName}`;
      case 'callback':
        return `${base}/callbackresponse?name=${options.callbackResponseName}`;
      case 'recover':
        return `${base}/${platform.value}/Collection/${scheduleCollection}/Name/${scheduleName}/Job/${options.jobId}/Recover`;
      default:
        return '';
    }
  };

  const resetStates = () => {
    setJobsData([]);
    page = 0;
  };

  const handleSearch = async () => {
    const newErrors = {
      platform: !platform,
      scheduleCollection: !scheduleCollection,
      scheduleName: !scheduleName,
    };

    setErrors(newErrors);
    const hasError = !!Object.values(newErrors).filter(error => error).length;

    if (!hasError) {
      resetStates();
      registry.get(getUrl('registry'));
      jobs.get(getUrl('jobs'));
      setHasSearched(true);
    }
  };

  const getMoreJobs = () => {
    jobs.get(getUrl('jobs', { continuationToken }));
  };

  return (
    <div>
      <div className="form-group row">
        <label className="col-sm-3 col-form-label">Platform</label>
        <div className="col-sm-9 col-md-6">
          <Select
            id="schedule-collection"
            name="schedule-collection"
            onChange={data => setPlatform(data)}
            options={[
              { label: 'Office 365', value: 'o365' },
              { label: 'NWC', value: 'nwc' },
              { label: 'EVM', value: 'evm' },
            ]}
            value={platform}
          />
          {errors.platform && <div className="error-label">Platform is required</div>}
        </div>
      </div>

      <div className="form-group row">
        <label className="col-sm-3 col-form-label">
          Schedule Collection
          <Tooltip
            placement="bottom"
            title="Tenant ID"
            fullWidth={false}
            type="info"
            parentContainerClass="form-group"
            showOnClick
          >
            <div className="helper-tooltip">?</div>
          </Tooltip>
        </label>
        <div className="col-sm-9 col-md-6">
          <Input
            id="schedule-collection"
            name="schedule-collection"
            type="text"
            onChange={data => setScheduleCollection(data)}
            value={scheduleCollection}
          />
          {errors.scheduleCollection && (
            <div className="error-label">Schedule Collection is required</div>
          )}
        </div>
      </div>

      <div className="form-group row">
        <label className="col-sm-3 col-form-label">
          Schedule Name
          <Tooltip
            placement="bottom"
            title="[workflowId_scheduleDefinitionId] for Office 365. [tenandId_workflowId] for NWC. e.g. 1234567890_1234567890. [notificationId] for EVM."
            fullWidth={false}
            maxWidth={250}
            type="info"
            parentContainerClass="form-group"
            showOnClick
          >
            <div className="helper-tooltip">?</div>
          </Tooltip>
        </label>
        <div className="col-sm-9 col-md-6">
          <Input
            id="schedule-name"
            name="schedule-name"
            type="text"
            value={scheduleName}
            onChange={data => setScheduleName(data)}
          />
          {errors.scheduleName && <div className="error-label">Schedule Name is required</div>}
        </div>
      </div>

      <p>&nbsp;</p>

      <div className="form-group row">
        <div className="col-sm-3">&nbsp;</div>
        <div className="col-sm-9 col-md-6">
          <Button onClick={handleSearch}>Search</Button>
        </div>
      </div>

      {hasSearched && (
        <Fragment>
          <div className="section">
            <h2>Registry</h2>
            <Registry
              getUrl={getUrl}
              data={registry.response.data}
              isLoading={registry.isLoading}
              error={registry.error}
            />
          </div>
          <div className="section">
            <h2>Jobs</h2>
            <Jobs
              getUrl={getUrl}
              data={jobsData}
              isLoading={jobs.isLoading}
              error={registry.error || jobs.error}
              getNextPage={getMoreJobs}
              hasNextPage={!!continuationToken}
            />
          </div>
        </Fragment>
      )}

      <div className="scroll-to">
        <Button onClick={scroll(UP)}>↑</Button>
        <Button onClick={scroll(DOWN)}>↓</Button>
      </div>
    </div>
  );
}

export default Home;
